import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';
import { Box } from '@material-ui/core';

import { HOME } from '../../../constants/routes';
import { useAppWidthClass } from '../../../utils/hooks';

import { PayNowButton } from '../../common';
import { HOME_PAY_NOW_LINK } from '../../home/Payment';
import {
  HEADER_BAR_HEIGHT,
  HEADER_CONTACTS_HEIGHT,
  HEADER_NAVIGATION_HEIGHT,
  HEADER_PAY_BAR_HEIGHT
} from '../../../constants/ui';
import { hasPayButtonInHeader } from '../../../utils/ui';

const BUTTON_MARGIN = 8;

const useStyles = makeStyles(theme => ({
  common: {
    textAlign: 'center',
    background: theme.palette.background.default,
    boxSizing: 'border-box',
    transition: theme.transitions.custom.header
  },
  homeBarWrapper: {
    display: 'none',
    boxShadow: theme.customShadows.menu
  },
  homeBarVisible: {
    display: 'block',
    animation: '$homeBarIn 120ms forwards'
  },
  '@keyframes homeBarIn': {
    '0%': {
      height: 0
    },
    '100%': {
      height: HEADER_PAY_BAR_HEIGHT
    }
  },
  defaultBar: {
    height: HEADER_PAY_BAR_HEIGHT
  },
  defaultBarSticky: {
    position: 'fixed',
    left: 0,
    right: 0,
    zIndex: 1,
    boxShadow: theme.customShadows.menu
  },
  button: {
    marginTop: BUTTON_MARGIN
  },
  mobileButton: {
    width: 256
  }
}));

const PayNowBar = ({
  isInHeader,
  currentRoute,
  isDefaultPayBarSticky,
  setIsDefaultPayBarSticky,
  headerAnimationShift
}) => {
  const classes = useStyles();
  const { isMobileWidth } = useAppWidthClass();

  const barRef = useRef();
  const [isHomeBarVisible, setIsHomeBarVisible] = useState(false);

  const isHome = currentRoute === HOME;
  const isHomeHeaderOrDefaultHeader = isHome === !!isInHeader;
  const isRouteWithPayBar = hasPayButtonInHeader(
    currentRoute,
    false,
    isHomeHeaderOrDefaultHeader
  );

  const onScrollForHomePageInHeader = useCallback(() => {
    const payNowLink = document.getElementById(HOME_PAY_NOW_LINK);
    if (payNowLink) {
      if (
        window.scrollY >=
        payNowLink.offsetTop - HEADER_NAVIGATION_HEIGHT - BUTTON_MARGIN
      ) {
        if (!isHomeBarVisible) {
          setIsHomeBarVisible(true);
        }
      } else {
        if (isHomeBarVisible) {
          setIsHomeBarVisible(false);
        }
      }
    }
  }, [isHomeBarVisible, setIsHomeBarVisible]);

  const onScrollDefault = useCallback(() => {
    if (barRef && barRef.current) {
      if (
        window.scrollY >
        HEADER_BAR_HEIGHT + (isMobileWidth ? 0 : HEADER_CONTACTS_HEIGHT)
      ) {
        if (!isDefaultPayBarSticky) {
          setIsDefaultPayBarSticky(true);
        }
      } else {
        if (isDefaultPayBarSticky) {
          setIsDefaultPayBarSticky(false);
        }
      }
    }
  }, [isMobileWidth, barRef, isDefaultPayBarSticky, setIsDefaultPayBarSticky]);

  useEffect(() => {
    if (isRouteWithPayBar && isHome && isInHeader) {
      window.addEventListener('scroll', onScrollForHomePageInHeader);
    } else if (isRouteWithPayBar && !isHome && !isInHeader) {
      window.addEventListener('scroll', onScrollDefault);
    }
    return () => {
      if (isRouteWithPayBar && isHome && isInHeader) {
        window.removeEventListener('scroll', onScrollForHomePageInHeader);
      } else if (isRouteWithPayBar && !isHome && !isInHeader) {
        window.removeEventListener('scroll', onScrollDefault);
      }
    };
  }, [
    isMobileWidth,
    isRouteWithPayBar,
    isInHeader,
    isHome,
    isHomeBarVisible,
    isDefaultPayBarSticky,
    barRef
  ]);

  const top = Math.max(
    HEADER_BAR_HEIGHT +
      HEADER_NAVIGATION_HEIGHT +
      (isMobileWidth ? 0 : HEADER_CONTACTS_HEIGHT) -
      headerAnimationShift,
    HEADER_NAVIGATION_HEIGHT
  );

  return isRouteWithPayBar && isHomeHeaderOrDefaultHeader ? (
    <Box
      ref={barRef}
      className={classNames(
        classes.common,
        isHome && classes.homeBarWrapper,
        isHomeBarVisible && classes.homeBarVisible,
        !isHome && classes.defaultBar,
        isDefaultPayBarSticky && classes.defaultBarSticky
      )}
      style={{ top: `${top}px` }}
    >
      <PayNowButton
        id="page.component.home-header.pay-now-link"
        className={classNames(
          classes.button,
          isMobileWidth && classes.mobileButton
        )}
      />
    </Box>
  ) : null;
};

export default PayNowBar;
